import dayjs from 'dayjs';

/**
 * Format booking status message for display
 * @param {Object} slotStatus - Status object from getSlotStatus
 * @param {Object} schoolSettings - School booking settings
 * @returns {string} User-friendly status message
 */
export const formatBookingStatusMessage = (slotStatus, schoolSettings) => {
    switch (slotStatus.type) {
        case 'owner-confirmed':
            return 'Twój potwierdzony trening';
        case 'owner-pending':
            return 'Twoja rezerwacja oczekuje na potwierdzenie';
        case 'locked':
            return 'Termin zablokowany przez trenera';
        case 'confirmed-other':
            return 'Termin zajęty przez innego właściciela';
        case 'past':
            return 'Termin już minął';
        case 'unavailable':
            if (schoolSettings?.minBookingDaysInAdvance > 0) {
                return `Rezerwacja możliwa z ${schoolSettings.minBookingDaysInAdvance} dni wyprzedzeniem`;
            }
            return 'Termin niedostępny do rezerwacji';
        case 'free':
            if (slotStatus.bookingCount > 0) {
                return `Wolny termin (${slotStatus.bookingCount} oczekujących)`;
            }
            return 'Wolny termin - kliknij aby zarezerwować';
        default:
            return '';
    }
};

/**
 * Get cancellation deadline for a training
 * @param {Object} training - Training object
 * @param {Object} schoolSettings - School settings
 * @returns {Object} {canCancel: boolean, deadline: dayjs|null, message: string}
 */
export const getCancellationInfo = (training, schoolSettings) => {
    if (!training) return { canCancel: false, deadline: null, message: '' };

    const trainingDate = dayjs(training.startTime);
    const now = dayjs();

    // Unconfirmed trainings can always be cancelled
    if (!training.confirmed) {
        return {
            canCancel: true,
            deadline: null,
            message: 'Możesz anulować niepotwierdzony trening w dowolnym momencie'
        };
    }

    // Check school cancellation policy
    if (!schoolSettings || !schoolSettings.minDaysBeforeCancel) {
        return {
            canCancel: true,
            deadline: null,
            message: 'Możesz anulować trening w dowolnym momencie'
        };
    }

    const deadline = trainingDate.subtract(schoolSettings.minDaysBeforeCancel, 'day').startOf('day');
    const canCancel = now.isBefore(deadline);

    if (canCancel) {
        return {
            canCancel: true,
            deadline,
            message: `Możesz anulować do ${deadline.format('DD.MM.YYYY')}`
        };
    } else {
        return {
            canCancel: false,
            deadline,
            message: `Termin anulowania minął (był ${deadline.format('DD.MM.YYYY')})`
        };
    }
};

/**
 * Group selected slots by day for summary display
 * @param {Array} selectedSlots - Array of slot time strings
 * @returns {Array} Array of {date, dayName, slots} objects
 */
export const groupSlotsByDay = (selectedSlots) => {
    const grouped = {};

    selectedSlots.forEach(slotStr => {
        const slot = dayjs(slotStr, 'YYYY-MM-DDTHH:mm:ss');
        const dateKey = slot.format('YYYY-MM-DD');

        if (!grouped[dateKey]) {
            grouped[dateKey] = {
                date: slot.startOf('day'),
                dayName: slot.format('dddd'),
                slots: []
            };
        }

        grouped[dateKey].slots.push(slot);
    });

    // Sort by date and sort slots within each day
    return Object.values(grouped)
        .sort((a, b) => a.date.diff(b.date))
        .map(day => ({
            ...day,
            slots: day.slots.sort((a, b) => a.diff(b))
        }));
};

/**
 * Format multiple booking summary for confirmation
 * @param {Array} selectedSlots - Array of slot time strings
 * @param {Object} service - Selected service
 * @returns {string} Formatted summary
 */
export const formatMultipleBookingSummary = (selectedSlots, service) => {
    const groupedSlots = groupSlotsByDay(selectedSlots);
    const lines = [`Wybrane terminy (${selectedSlots.length}):`];

    groupedSlots.forEach(({ date, dayName, slots }) => {
        lines.push(`\n${dayName}, ${date.format('DD.MM.YYYY')}:`);
        slots.forEach(slot => {
            lines.push(`  • ${slot.format('HH:mm')}`);
        });
    });

    if (service) {
        lines.push(`\nRodzaj treningu: ${service.serviceName}`);
        if (service.price) {
            const totalPrice = service.price * selectedSlots.length;
            lines.push(`Łączny koszt: ${totalPrice} zł`);
        }
    }

    return lines.join('\n');
};

/**
 * Check if a booking action is allowed
 * @param {string} action - 'book' or 'cancel'
 * @param {Object} context - {slotTime, training, schoolSettings}
 * @returns {Object} {allowed: boolean, reason: string}
 */
export const isBookingActionAllowed = (action, context) => {
    const { slotTime, training, schoolSettings } = context;
    const now = dayjs();

    if (action === 'book') {
        const slot = dayjs(slotTime);

        // Check if in the past
        if (slot.isBefore(now)) {
            return { allowed: false, reason: 'Nie można rezerwować przeszłych terminów' };
        }

        // Check advance booking requirement
        if (schoolSettings?.minBookingDaysInAdvance > 0) {
            const minDate = now.add(schoolSettings.minBookingDaysInAdvance, 'day').startOf('day');
            if (slot.startOf('day').isBefore(minDate)) {
                return {
                    allowed: false,
                    reason: `Rezerwacja wymaga ${schoolSettings.minBookingDaysInAdvance} dni wyprzedzenia`
                };
            }
        }

        // Check month restrictions
        if (schoolSettings?.unlockedMonths && schoolSettings.unlockedMonths.length > 0) {
            const monthNames = [
                'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
                'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
            ];
            const slotMonth = monthNames[slot.month()];
            if (!schoolSettings.unlockedMonths.includes(slotMonth)) {
                return {
                    allowed: false,
                    reason: 'Rezerwacje w tym miesiącu są zablokowane'
                };
            }
        }

        return { allowed: true, reason: '' };
    }

    if (action === 'cancel' && training) {
        const cancellationInfo = getCancellationInfo(training, schoolSettings);
        return {
            allowed: cancellationInfo.canCancel,
            reason: cancellationInfo.message
        };
    }

    return { allowed: false, reason: 'Nieznana akcja' };
};

/**
 * Calculate statistics for selected slots
 * @param {Array} selectedSlots - Array of slot time strings
 * @param {Object} service - Selected service with price
 * @returns {Object} Statistics object
 */
export const calculateBookingStats = (selectedSlots, service) => {
    const stats = {
        totalSlots: selectedSlots.length,
        totalHours: selectedSlots.length, // Assuming 1 hour per slot
        totalPrice: 0,
        dateRange: null,
        daysSpan: 0
    };

    if (selectedSlots.length === 0) return stats;

    // Calculate price
    if (service?.price) {
        stats.totalPrice = service.price * selectedSlots.length;
    }

    // Calculate date range
    const dates = selectedSlots.map(s => dayjs(s, 'YYYY-MM-DDTHH:mm:ss'));
    const minDate = dayjs.min(...dates);
    const maxDate = dayjs.max(...dates);

    stats.dateRange = {
        start: minDate,
        end: maxDate
    };

    stats.daysSpan = maxDate.diff(minDate, 'day') + 1;

    return stats;
};

/**
 * Validate dog selection for booking
 * @param {string} selectedDogId - Selected dog ID
 * @param {Array} ownerDogs - List of owner's dogs
 * @returns {Object} {valid: boolean, message: string}
 */
export const validateDogSelection = (selectedDogId, ownerDogs) => {
    if (!selectedDogId) {
        if (ownerDogs.length === 0) {
            return { valid: false, message: 'Nie masz żadnych pupili zarejestrowanych w systemie' };
        }
        return { valid: false, message: 'Wybierz pupila przed rezerwacją' };
    }

    const selectedDog = ownerDogs.find(dog => dog.id === selectedDogId);
    if (!selectedDog) {
        return { valid: false, message: 'Wybrany pupil nie został znaleziony' };
    }

    return { valid: true, message: '', dog: selectedDog };
};